import React from 'react';
import {Segmented, Tooltip} from 'antd';
import {DownOutlined, ExclamationCircleOutlined} from '@ant-design/icons';
import {getTagForAIProcessResponse} from "@src/components/Common/Tags";
import './PipelineStageSelector.css';

interface PipelineStageSelectorProps {
    current: -1 | 0 | 1 | 2;
    onChange: (value: number | string) => void;
    dataPipeline: any;
    showOverview?: boolean;
    /** stages carry a caret that folds their own details open under the strip */
    expandable?: boolean;
    expandedStage?: -1 | 0 | 1 | 2 | null;
    onToggleExpand?: (stage: 0 | 1 | 2) => void;
}

const formatLabel = (key: string) =>
    key
        .replace(/[_-]/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());

const renderValue = (value: any) => {
    if (value === null || value === undefined || value === "")
        return <span style={{ color: "rgba(255,255,255,0.45)", fontStyle: "italic" }}>—</span>;
    if (typeof value === "boolean")
        return <span style={{ color: value ? "#52c41a" : "#ff7875" }}>{value ? "Yes" : "No"}</span>;
    if (typeof value === "object")
        return (
            <pre style={{ margin: 0, fontFamily: "monospace", fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word", color: "#e6f7ff" }}>
                {JSON.stringify(value, null, 2)}
            </pre>
        );
    return <span style={{ color: "#fff" }}>{String(value)}</span>;
};

const renderBody = (body: any) => {
    const isPlainObject =
        body !== null && typeof body === "object" && !Array.isArray(body);

    if (!isPlainObject) {
        const text = typeof body === "string" ? body : JSON.stringify(body, null, 2);
        return (
            <div style={{ fontSize: 13, lineHeight: 1.5, color: "#fff", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {text}
            </div>
        );
    }

    const entries = Object.entries(body);

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {entries.map(([key, value]) => (
                <div key={key} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "#ffd666" }}>
                        {formatLabel(key)}
                    </span>
                    <div style={{ fontSize: 13, lineHeight: 1.5 }}>{renderValue(value)}</div>
                </div>
            ))}
        </div>
    );
};

const renderStageState = (response: any) => {
    const state = response?.state;
    const tag = getTagForAIProcessResponse(state);

    if (state !== "FAILED" && state !== "SCHEDULED")
        return tag;

    const body = response?.body;
    const hasBody = body !== undefined && body !== null && body !== "";

    if (!hasBody)
        return tag;

    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            {tag}
            <Tooltip
                color="#1f1f1f"
                title={
                    <div style={{ maxHeight: 320, overflow: "auto", padding: "4px 2px" }}>
                        {renderBody(body)}
                    </div>
                }
                styles={{ root: { maxWidth: 480 } }}
            >
                <ExclamationCircleOutlined style={{ color: "#faad14", cursor: "pointer" }} />
            </Tooltip>
        </span>
    );
};

const STAGES: { value: 0 | 1 | 2; name: string; key: string }[] = [
    { value: 0, name: 'Config', key: 'ai_processor_response_config' },
    { value: 1, name: 'Verify', key: 'ai_processor_response_verify' },
    { value: 2, name: 'Execution', key: 'ai_processor_response_execution' },
];

const PipelineStageSelector = ({
    current,
    onChange,
    dataPipeline,
    showOverview = false,
    expandable = false,
    expandedStage = null,
    onToggleExpand,
}: PipelineStageSelectorProps) => {
    const overviewOption = {
        label: <span className="stage-label">📊 Overview</span>,
        value: -1
    };

    /* One click, two meanings, told apart by where you already are: on another
       stage it means go there, and it goes there folded; on the stage you are
       already on it means show me this run — anywhere on the stage, the caret
       only saying which way that will go. */
    const activate = (stage: 0 | 1 | 2) => {
        if (current !== stage) onChange(stage);
        else onToggleExpand?.(stage);
    };

    return (
        <div className="pipeline-stage-selector">
            <Segmented
                block
                options={[
                    ...(showOverview ? [overviewOption] : []),
                    ...STAGES.map(({ value, name, key }) => {
                        const open = expandedStage === value;
                        return {
                            value,
                            label: (
                                <span
                                    className={`stage-label${value < 2 ? ' stage-flows-next' : ''}`}
                                    onClick={expandable ? () => activate(value) : undefined}
                                >
                                    <span className="stage-num">{value + 1}</span>
                                    {name}
                                    {renderStageState(dataPipeline?.[key])}
                                    {/* the caret only says which way the click will
                                        go — but it holds the keyboard's way in,
                                        since the label's radio is not inside it */}
                                    {expandable && (
                                        <span
                                            role="button"
                                            tabIndex={0}
                                            aria-label={open ? 'Hide stage details' : 'Show stage details'}
                                            aria-expanded={open}
                                            className={`stage-caret${open ? ' stage-caret--open' : ''}`}
                                            onClick={(e) => { e.stopPropagation(); activate(value); }}
                                            onKeyDown={(e) => {
                                                if (e.key !== 'Enter' && e.key !== ' ') return;
                                                e.preventDefault();
                                                activate(value);
                                            }}
                                        >
                                            <DownOutlined />
                                        </span>
                                    )}
                                </span>
                            ),
                        };
                    }),
                ]}
                value={current}
                onChange={(val) => onChange(val)}
            />
        </div>
    );
};

export default PipelineStageSelector;
